import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useAuth } from '../../hooks/useAuth';
import { visibleNavItems, isPathActive } from './navConfig';

/**
 * Floating glass dock shown below the md breakpoint. Shows the user's first
 * four destinations plus a Menu button that opens the full drawer.
 */
const BottomNav = ({ onOpenMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const items = visibleNavItems(user?.role).slice(0, 4);

  const renderButton = ({ key, icon, label, active, highlight, onClick }) => (
    <ButtonBase
      key={key}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.4,
        py: 1,
        borderRadius: '14px',
        color: active ? 'primary.main' : 'text.secondary',
        transition: 'color 180ms ease, transform 180ms ease',
        '&:active': { transform: 'scale(0.94)' },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 26,
          borderRadius: '999px',
          display: 'grid',
          placeItems: 'center',
          transition: 'background-color 200ms ease',
          bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.14) : 'transparent',
          position: 'relative',
          '& svg': { fontSize: 21 },
        }}
      >
        {icon}
        {highlight && !active && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 1,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'error.main',
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.64rem',
          fontWeight: active ? 700 : 500,
          letterSpacing: '0.01em',
          lineHeight: 1,
          maxWidth: '100%',
        }}
        noWrap
      >
        {label}
      </Typography>
    </ButtonBase>
  );

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.appBar,
        left: 12,
        right: 12,
        bottom: 'max(10px, env(safe-area-inset-bottom))',
        display: { xs: 'block', md: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: '20px',
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.85 : 0.85),
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: (theme) => `1px solid ${theme.custom.cardBorder}`,
          boxShadow: (theme) => theme.custom.hoverShadow,
        }}
      >
        {items.map((item) =>
          renderButton({
            key: item.path,
            icon: item.icon,
            label: item.shortText || item.text,
            highlight: item.highlight,
            active: isPathActive(item.path, location.pathname),
            onClick: () => navigate(item.path),
          })
        )}
        {renderButton({
          key: 'menu',
          icon: <MenuRoundedIcon />,
          label: 'Menu',
          active: false,
          onClick: onOpenMenu,
        })}
      </Box>
    </Box>
  );
};

export default BottomNav;
